/**
 * UrlImportTab Component
 * Tab content for importing URL-based documents
 * Pure presentational component
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Link, TextField, Typography } from '@mui/material';
import { UrlImportTabProps } from '../types';
import { signedInSourceOf } from '../../../../utils/documents/signedInSource';
import { usePermissions } from '../../../../hooks/permissions/usePermissions';

export const UrlImportTab: React.FC<UrlImportTabProps> = ({
  value,
  onChange,
  error
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  // A link into SharePoint, OneDrive or Teams cannot work here: the address is
  // fetched without signing in. Say so before the attempt, and point an
  // administrator at the document sources, which do sign in.
  const signedInSource = signedInSourceOf(value);
  const canConfigureSources = hasPermission('document_sources');

  return (
    <Box>
      <TextField
        required
        fullWidth
        label={t('documents.dialog.url')}
        name="url"
        value={value}
        onChange={onChange}
        helperText={error || t('documents.dialog.urlHelp')}
        error={Boolean(error)}
      />
      {signedInSource && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t(`documents.upload.signedInSource.${signedInSource}`)}{' '}
          {canConfigureSources ? (
            <Link component={RouterLink} to="/document-source-config">
              {t('documents.upload.signedInSource.openSources')}
            </Link>
          ) : (
            t('documents.upload.signedInSource.askAdmin')
          )}
        </Alert>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
        {t('documents.upload.urlInfo')}
      </Typography>
    </Box>
  );
};
